/**
 * Default Governance Policy — Migrates all scattered constants into one object.
 *
 * ARCHITECTURE v4.0 — Phase 1, Step 1.2
 *
 * Sources migrated:
 * - utils.ts: TIER_MIN_EXPLANATION_LENGTH, TIER_MIN_DISTRACTOR_LENGTH, SEVERITY_WEIGHTS
 * - visualRequiredRules.ts: VISUAL_REQUIRED_FAMILIES
 * - familySupportRegistry.ts: requiresVisual/requiresHints flags
 * - governanceValidator.ts: inline thresholds (forbiddenB6Vocabulary, text length limits)
 * - questionTypeDefinitions.ts: type-specific required fields
 * - VISUAL_GOVERNANCE_MAP.json: per-family visual rules
 */

import type { GovernancePolicy } from './policyTypes'
import type { GateThresholds } from './issueContract'
import { DEFAULT_GATE_THRESHOLDS } from './issueContract'

// ─── Gate Thresholds ──────────────────────────────────────────────────────────

const GATES: GateThresholds = {
  productionMinScore: 70, // raised from 60
  assessmentMinScore: 82, // raised from 75
  reviewCoachSeverityThreshold: 45, // lowered from 50 — more questions flagged for review
  assessmentMinQuestionsPerFamily: 3,
}

// ─── Default Policy ───────────────────────────────────────────────────────────

export const DEFAULT_POLICY: GovernancePolicy = {
  version: '1.0.0',

  // ── Vocabulary ────────────────────────────────────────────────────────────
  vocab: {
    forbiddenByTierBand: {
      basic: [
        // Original
        'notatie',
        'commutatief',
        'associatief',
        'proportioneel',
        'distributief',
        'exponentieel',
        // NEW from CONTENT_GOVERNANCE_FRAMEWORK §1.1 — forbidden in groep 6 question text
        'strategie',
        'posities',
        'algoritme',
        'noemer',
        'teller',
        'decimaalgetal',
      ],
      intermediate: [
        // Original
        'commutatief',
        'associatief',
        'distributief',
        // NEW from CONTENT_GOVERNANCE_FRAMEWORK §1.1 — still too complex for groep 7
        'algoritme',
        'exponentieel',
      ],
      advanced: [],
      expert: [],
    },
  },

  // ── Content Budgets ───────────────────────────────────────────────────────
  contentBudgets: {
    maxQuestionLengthByTierBand: {
      basic: 200, // Tiers 1-4
      intermediate: 400, // Tiers 5-8
      advanced: 600, // Tiers 9-12
      expert: 800, // Tiers 13-16
    },
    minExplanationLengthByTierBand: {
      basic: 30,
      intermediate: 50,
      advanced: 80,
      expert: 100,
    },
    minDistractorLengthByTierBand: {
      basic: 15,
      intermediate: 25,
      advanced: 35,
      expert: 45,
    },
    moduleOverrides: {
      Taalbegrip: {
        maxQuestionLengthByTierBand: {
          basic: 200,
          intermediate: 400,
          advanced: 600,
          expert: 800,
        },
      },
    },
  },

  // ── Visual Policy ─────────────────────────────────────────────────────────
  // Merged from: visualRequiredRules.ts + VISUAL_GOVERNANCE_MAP.json + familySupportRegistry.ts
  visual: {
    requiredFamilies: [
      // GET
      'GET-GROOT-POS',
      'GET-BREUK-GEL-PLUS',
      'GET-KOMMA-WAARDE',
      'GET-BREUK-ONG-PLUS',
      'GET-BREUK-KEER',
      'GET-VERH-SCHAAL',
      'GET-BREUK-DEEL',
      // MET
      'MET-LEN-OMREKEN',
      'MET-OPP-RECHT',
      'MET-INH-BLOK',
      'MET-INH-LITER',
      'MET-INH-CITER',
      'MET-MEETK-OMTREK',
      'MET-MEETK-OPP',
      'MET-SCHAAL-OPP',
      // TGL
      'TGL-TIJD-ANALOOG',
      'TGL-GELD-TERUG',
      'TGL-TIJD-DIGITAAL',
      'TGL-GELD-KOMMA',
      // STU
      'STU-TABEL',
      'STU-GRAFIEK',
      'STU-KAART',
    ],
    familyRules: {
      // GET visual rules
      'GET-GROOT-POS': {
        stimulusRequired: false,
        supportRequired: true,
        supportDefault: 'numberline',
        supportShowWhen: 'wrong',
      },
      'GET-BREUK-BASIS': {
        stimulusRequired: true,
        stimulusDefault: 'fraction',
        supportRequired: false,
      },
      'GET-BREUK-GEL-PLUS': {
        stimulusRequired: true,
        stimulusDefault: 'fraction',
        supportRequired: true,
        supportDefault: 'fraction',
        supportShowWhen: 'always',
      },
      'GET-KOMMA-WAARDE': {
        stimulusRequired: false,
        supportRequired: true,
        supportDefault: 'numberline',
        supportShowWhen: 'wrong',
      },
      'GET-BREUK-ONG-PLUS': {
        stimulusRequired: false,
        supportRequired: true,
        supportDefault: 'fraction',
        supportShowWhen: 'wrong',
      },
      'GET-BREUK-KEER': {
        stimulusRequired: true,
        stimulusDefault: 'fraction',
        supportRequired: true,
        supportDefault: 'fraction',
        supportShowWhen: 'always',
      },
      'GET-VERH-SCHAAL': {
        stimulusRequired: false,
        supportRequired: true,
        supportDefault: 'ratio',
        supportShowWhen: 'wrong',
      },
      'GET-BREUK-DEEL': {
        stimulusRequired: true,
        stimulusDefault: 'fraction',
        supportRequired: true,
        supportDefault: 'fraction',
        supportShowWhen: 'always',
      },
      // MET visual rules
      'MET-LEN-OMREKEN': {
        stimulusRequired: true,
        stimulusDefault: 'stairs',
        supportRequired: true,
        supportDefault: 'stairs',
        supportShowWhen: 'always',
      },
      'MET-OPP-RECHT': {
        stimulusRequired: false,
        supportRequired: true,
        supportDefault: 'geo',
        supportShowWhen: 'wrong',
      },
      'MET-INH-BLOK': {
        stimulusRequired: false,
        supportRequired: true,
        supportDefault: 'stairs',
        supportShowWhen: 'wrong',
      },
      'MET-INH-LITER': {
        stimulusRequired: false,
        supportRequired: true,
        supportDefault: 'stairs',
        supportShowWhen: 'wrong',
      },
      'MET-INH-CITER': {
        stimulusRequired: false,
        supportRequired: true,
        supportDefault: 'stairs',
        supportShowWhen: 'wrong',
      },
      'MET-MEETK-OMTREK': {
        stimulusRequired: false,
        supportRequired: true,
        supportDefault: 'geo',
        supportShowWhen: 'wrong',
      },
      'MET-MEETK-OPP': {
        stimulusRequired: false,
        supportRequired: true,
        supportDefault: 'geo',
        supportShowWhen: 'wrong',
      },
      'MET-SCHAAL-OPP': {
        stimulusRequired: false,
        supportRequired: true,
        supportDefault: 'ratio',
        supportShowWhen: 'wrong',
      },
      // TGL visual rules
      'TGL-TIJD-ANALOOG': {
        stimulusRequired: true,
        stimulusDefault: 'clock',
        supportRequired: true,
        supportDefault: 'clock',
        supportShowWhen: 'wrong',
      },
      'TGL-TIJD-DIGITAAL': {
        stimulusRequired: true,
        stimulusDefault: 'clock',
        supportRequired: false,
      },
      'TGL-GELD-TERUG': {
        stimulusRequired: false,
        supportRequired: true,
        supportDefault: 'money',
        supportShowWhen: 'wrong',
      },
      'TGL-GELD-MAAK': {
        stimulusRequired: false,
        supportRequired: true,
        supportDefault: 'money',
        supportShowWhen: 'wrong',
      },
      'TGL-GELD-WISSEL': {
        stimulusRequired: false,
        supportRequired: true,
        supportDefault: 'money',
        supportShowWhen: 'wrong',
      },
      'TGL-GELD-KOMMA': {
        stimulusRequired: false,
        supportRequired: true,
        supportDefault: 'money',
        supportShowWhen: 'wrong',
      },
      // STU visual rules
      'STU-TABEL': {
        stimulusRequired: true,
        stimulusDefault: 'ratio',
        supportRequired: false,
      },
      'STU-GRAFIEK': {
        stimulusRequired: true,
        stimulusDefault: 'stairs',
        supportRequired: false,
      },
      'STU-KAART': {
        stimulusRequired: true,
        stimulusDefault: 'geo',
        supportRequired: false,
      },
    },
  },

  // ── Support Policy ────────────────────────────────────────────────────────
  // Migrated from familySupportRegistry.ts requiresVisual/requiresHints flags
  support: {
    familyRules: {
      // SWW — all require hints
      'SWW-TT-STAM': {
        hintsRequired: true,
        explanationRequired: true,
        visualSupportRequired: false,
      },
      'SWW-TT-INV-JIJ': {
        hintsRequired: true,
        explanationRequired: true,
        visualSupportRequired: false,
      },
      'SWW-VT-STERK': {
        hintsRequired: true,
        explanationRequired: true,
        visualSupportRequired: false,
      },
      'SWW-VT-ZWAK-KOF': {
        hintsRequired: true,
        explanationRequired: true,
        visualSupportRequired: false,
      },
      'SWW-VD-KOF': {
        hintsRequired: true,
        explanationRequired: true,
        visualSupportRequired: false,
      },
      'SWW-TT-DSTAM': {
        hintsRequired: true,
        explanationRequired: true,
        visualSupportRequired: false,
      },
      // SPR — all require hints
      'SPR-KLANK-JAGER': {
        hintsRequired: true,
        explanationRequired: true,
        visualSupportRequired: false,
      },
      'SPR-KLANK-BAKKER': {
        hintsRequired: true,
        explanationRequired: true,
        visualSupportRequired: false,
      },
      // GET — mix of hints + visuals
      'GET-GROOT-POS': {
        hintsRequired: true,
        explanationRequired: true,
        visualSupportRequired: true,
      },
      'GET-BREUK-GEL-PLUS': {
        hintsRequired: true,
        explanationRequired: true,
        visualSupportRequired: true,
      },
      'GET-KOMMA-WAARDE': {
        hintsRequired: true,
        explanationRequired: true,
        visualSupportRequired: true,
      },
      // MET — require visuals
      'MET-LEN-OMREKEN': {
        hintsRequired: true,
        explanationRequired: true,
        visualSupportRequired: true,
      },
      'MET-OPP-RECHT': {
        hintsRequired: true,
        explanationRequired: true,
        visualSupportRequired: true,
      },
      // TGL — mix
      'TGL-TIJD-ANALOOG': {
        hintsRequired: true,
        explanationRequired: true,
        visualSupportRequired: true,
      },
      'TGL-GELD-TERUG': {
        hintsRequired: true,
        explanationRequired: true,
        visualSupportRequired: true,
      },
    },
  },

  // ── Question Type Policy ──────────────────────────────────────────────────
  questionType: {
    typeRules: {
      multiple_choice: {
        requiredFields: ['question', 'options', 'correctAnswer'],
        assessmentEligible: true,
      },
      fill_in: {
        requiredFields: ['question', 'correctAnswer'],
        assessmentEligible: false,
      },
      ordering: {
        requiredFields: ['question', 'data'],
        assessmentEligible: false,
        dataFields: { required: ['items', 'correctOrder'], optional: [] },
      },
      matching: {
        requiredFields: ['question', 'data'],
        assessmentEligible: false,
        dataFields: {
          required: ['leftItems', 'rightItems', 'correctMatches'],
          optional: ['distractors'],
        },
      },
      table_reading: {
        requiredFields: ['question', 'data', 'options', 'correctAnswer'],
        assessmentEligible: true,
        dataFields: {
          required: ['headers', 'rows'],
          optional: ['caption', 'highlightCell'],
        },
      },
      bar_chart: {
        requiredFields: ['question', 'data', 'options', 'correctAnswer'],
        assessmentEligible: true,
        dataFields: {
          required: ['labels', 'values'],
          optional: ['title', 'xLabel', 'yLabel'],
        },
      },
      line_chart: {
        requiredFields: ['question', 'data', 'options', 'correctAnswer'],
        assessmentEligible: true,
        dataFields: {
          required: ['labels', 'values'],
          optional: ['title', 'xLabel', 'yLabel'],
        },
      },
      clock: {
        requiredFields: ['question', 'data'],
        assessmentEligible: true,
        dataFields: {
          required: ['mode', 'target'],
          optional: ['analog', 'digital', 'options'],
        },
      },
      money: {
        requiredFields: ['question', 'data'],
        assessmentEligible: true,
        dataFields: {
          required: ['mode', 'targetCents', 'allowedDenoms'],
          optional: ['priceCents', 'paidCents', 'constraints'],
        },
      },
      schedule_reading: {
        requiredFields: ['question', 'data', 'options', 'correctAnswer'],
        assessmentEligible: true,
        dataFields: {
          required: ['headers', 'rows'],
          optional: ['title', 'highlightCell'],
        },
      },
      ratio_table: {
        requiredFields: ['question', 'data'],
        assessmentEligible: false,
        dataFields: {
          required: ['headers', 'rows', 'missingCell', 'correctValue'],
          optional: [],
        },
      },
      number_sequence: {
        requiredFields: ['question', 'data'],
        assessmentEligible: false,
        dataFields: {
          required: ['sequence', 'missingIndex', 'correctValue'],
          optional: ['rule'],
        },
      },
      drag_drop_fill: {
        requiredFields: ['question', 'data'],
        assessmentEligible: false,
        dataFields: {
          required: ['template', 'draggables', 'correctPlacements'],
          optional: ['distractors'],
        },
      },
      text_highlight: {
        requiredFields: ['question', 'data'],
        assessmentEligible: false,
        dataFields: {
          required: ['text', 'correctHighlights'],
          optional: ['maxHighlights'],
        },
      },
      cijferend_optellen: {
        requiredFields: ['question', 'data'],
        assessmentEligible: false,
        dataFields: {
          required: ['operand1', 'operand2'],
          optional: ['showSteps', 'currentStep'],
        },
      },
      cijferend_aftrekken: {
        requiredFields: ['question', 'data'],
        assessmentEligible: false,
        dataFields: {
          required: ['operand1', 'operand2'],
          optional: ['showSteps', 'currentStep'],
        },
      },
    },
  },

  // ── Notation Policy ───────────────────────────────────────────────────────
  notation: {
    latexFractionModules: ['Getalbegrip'],
    commaDecimalModules: ['Getalbegrip', 'Metriek', 'TijdGeld'],
    superscriptUnitModules: ['Metriek'],
    euroCommaModules: ['TijdGeld'],
  },

  // ── Distractor Policy ─────────────────────────────────────────────────────
  distractor: {
    minOptions: 2,
    maxOptions: 5,
    explanationsRequired: true,
    misconceptionRefRequired: true,
    modulePatterns: {
      Getalbegrip:
        'plaatswaarde|komma|afrond|decimaal|breuk|noemer|teller|macht|wortel|procent|verhouding',
      SpellingWW:
        'stam|kofschip|dt-regel|verdubbel|persoonsvorm|infinitief|voltooid deelwoord',
      SpellingRegel:
        'open lettergreep|gesloten lettergreep|trema|koppelteken|tussen-n|samengesteld',
      Metriek: 'formule|oppervlakte|omtrek|inhoud|eenheid|omrekenen',
      TijdGeld: 'eenheid|minuten|uren|seconden|euro|cent|wisselgeld',
      Taalbegrip: 'tekst|alinea|zin|grammatica|werkwoord|zelfstandig naamwoord',
      Studievaardig: 'strategie|samenvatten|hoofd(gedachte|idee)',
      Topo: 'provincie|hoofdstad|rivier|gebergte|oceaan|continent',
    },
  },

  // ── Explanation Policy ────────────────────────────────────────────────────
  explanation: {
    required: true,
    flagGeneric: true,
    requireDidactic: true,
    moduleRuleRefRequired: {
      SpellingWW: true,
      SpellingRegel: true,
      Metriek: true,
      TijdGeld: true,
      Taalbegrip: true,
      Studievaardig: true,
      Topo: true,
      Getalbegrip: true,
    },
    moduleCheckStrategyMinTier: {
      SpellingWW: 7, // SWW check strategy required from tier 7+
    },
  },

  // ── Gate Thresholds ───────────────────────────────────────────────────────
  gates: GATES,

  // ── Audience Profiles ─────────────────────────────────────────────────────
  audienceProfiles: [
    {
      id: 'default',
      name: 'Default',
      description: 'Standard governance profile for all learners (v1)',
    },
  ],

  // ── Feature Flags ─────────────────────────────────────────────────────────
  featureFlags: {
    ldgValidation: true,
    strictDataConvention: true, // ACTIVE — interactiveConfig is now a blocker
    enforceDistractorExplanations: true,
    enforceLatex: true, // ACTIVE — LaTeX required for fraction modules
    enforceUnitNotation: true, // ACTIVE — superscript units required for MET
    enforceSupportCoverage: true, // ACTIVE — support coverage validated
  },
}
